<template>
  <v-container fluid class="fill-height login-container">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="5" lg="4">
        <v-card elevation="8" class="login-card">
          <v-card-title class="text-h4 text-center py-6">
            <div class="d-flex flex-column align-center">
              <v-icon size="64" color="primary" class="mb-4">mdi-shield-check</v-icon>
              <span>Система ХАССП</span>
            </div>
          </v-card-title>

          <v-card-text class="px-8 pb-8">
            <v-form ref="loginForm" @submit.prevent="handleLogin">
              <!-- Email Field -->
              <v-text-field
                v-model="formData.email"
                label="Email"
                type="email"
                prepend-inner-icon="mdi-email"
                variant="outlined"
                density="comfortable"
                :error-messages="emailErrors"
                @blur="v$.email.$touch"
                @input="v$.email.$touch"
                class="mb-2"
              />

              <!-- Password Field -->
              <v-text-field
                v-model="formData.password"
                :label="'Пароль'"
                :type="showPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock"
                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showPassword = !showPassword"
                variant="outlined"
                density="comfortable"
                :error-messages="passwordErrors"
                @blur="v$.password.$touch"
                @input="v$.password.$touch"
                class="mb-2"
              />

              <!-- Error Alert -->
              <v-alert
                v-if="authStore.error"
                type="error"
                variant="tonal"
                closable
                @click:close="authStore.clearError()"
                class="mb-4"
              >
                {{ authStore.error }}
              </v-alert>

              <!-- Login Button -->
              <v-btn
                type="submit"
                color="primary"
                size="large"
                block
                :loading="authStore.loading"
                class="mb-3"
              >
                <v-icon left>mdi-login</v-icon>
                Войти
              </v-btn>

              <!-- Reset Password Link -->
              <div class="text-center">
                <v-btn
                  variant="text"
                  color="primary"
                  @click="showResetDialog = true"
                  size="small"
                >
                  Забыли пароль?
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Password Reset Dialog -->
    <v-dialog v-model="showResetDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          Восстановление пароля
        </v-card-title>

        <v-card-text>
          <v-form ref="resetForm" @submit.prevent="handleResetPassword">
            <v-text-field
              v-model="resetEmail"
              label="Email"
              type="email"
              prepend-inner-icon="mdi-email"
              variant="outlined"
              density="comfortable"
              :error-messages="resetEmailErrors"
              @blur="vReset$.resetEmail.$touch"
              @input="vReset$.resetEmail.$touch"
            />

            <v-alert
              v-if="resetSuccess"
              type="success"
              variant="tonal"
              class="mb-4"
            >
              Письмо с инструкциями отправлено на ваш email
            </v-alert>

            <v-alert
              v-if="resetError"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ resetError }}
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="closeResetDialog"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="handleResetPassword"
            :loading="resetLoading"
          >
            Отправить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength } from '@vuelidate/validators'

const router = useRouter()
const authStore = useAuthStore()

// Login Form Data
const formData = ref({
  email: '',
  password: '',
})

const showPassword = ref(false)

// Login Form Validation
const loginRules = {
  email: { 
    required, 
    email,
  },
  password: { 
    required,
    minLength: minLength(6),
  },
}

const v$ = useVuelidate(loginRules, formData)

// Computed error messages for login form
const emailErrors = computed(() => {
  const errors = []
  if (!v$.value.email.$dirty) return errors
  v$.value.email.required.$invalid && errors.push('Email обязателен')
  v$.value.email.email.$invalid && errors.push('Введите корректный email')
  return errors
})

const passwordErrors = computed(() => {
  const errors = []
  if (!v$.value.password.$dirty) return errors
  v$.value.password.required.$invalid && errors.push('Пароль обязателен')
  v$.value.password.minLength.$invalid && errors.push('Минимум 6 символов')
  return errors
})

// Reset Password Dialog
const showResetDialog = ref(false)
const resetEmail = ref('')
const resetLoading = ref(false)
const resetSuccess = ref(false)
const resetError = ref(null)

// Reset Form Validation
const resetRules = {
  resetEmail: { 
    required, 
    email,
  },
}

const vReset$ = useVuelidate(resetRules, { resetEmail })

const resetEmailErrors = computed(() => {
  const errors = []
  if (!vReset$.value.resetEmail.$dirty) return errors
  vReset$.value.resetEmail.required.$invalid && errors.push('Email обязателен')
  vReset$.value.resetEmail.email.$invalid && errors.push('Введите корректный email')
  return errors
})

// Handle Login
const handleLogin = async () => {
  const isValid = await v$.value.$validate()
  
  if (!isValid) {
    return
  }

  try {
    await authStore.login(formData.value.email, formData.value.password)
    router.push({ name: 'dashboard' })
  } catch (error) {
    console.error('Login error:', error)
  }
}

// Handle Password Reset
const handleResetPassword = async () => {
  const isValid = await vReset$.value.$validate()
  
  if (!isValid) {
    return
  }

  resetLoading.value = true
  resetError.value = null
  resetSuccess.value = false

  try {
    await authStore.resetPassword(resetEmail.value)
    resetSuccess.value = true
    setTimeout(() => {
      closeResetDialog()
    }, 3000)
  } catch (error) {
    resetError.value = error.response?.data?.detail || 'Ошибка отправки письма'
  } finally {
    resetLoading.value = false
  }
}

// Close Reset Dialog
const closeResetDialog = () => {
  showResetDialog.value = false
  resetEmail.value = ''
  resetSuccess.value = false
  resetError.value = null
  vReset$.value.$reset()
}
</script>

<style scoped>
.login-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.login-card {
  border-radius: 16px;
}
</style>

